import { formatElapsedClock } from "@cold-case/shared/manifest-display";
import Phaser from "phaser";

import type { CaseManifestV1, NpcInstanceV1 } from "@cold-case/shared/case-manifest";
import type { LeaderMirrorNpc } from "@cold-case/shared/player-follow";

import { flashNarrativeNudgedAck } from "../lib/follow-nudge-ui.js";
import { isNarrativeModalOpen } from "../lib/narrative-modal.js";

import type { CoherenceJournal } from "../lib/dialog-parser.js";

import { layoutClueToastIfVisible } from "./clue-toast.js";
import { isMobileNpcOverlay } from "./dialog-layout.js";
import { DESIGN_HEIGHT, DESIGN_WIDTH } from "./design-resolution.js";
import { layoutGameHud } from "./game-scene-layout.js";
import { layoutHudChrome, type HudLayoutMetrics } from "./hud-layout.js";
import {
  NpcCaseNotesPanel,
  type NpcCaseNotesOpenPayload,
} from "./npc-case-notes-panel.js";
import { NpcDialogPanel } from "./npc-dialog-panel.js";
import { UI_FONT, UI_TEXT } from "./ui-theme.js";

export type NpcDialogOpenPayload = {
  npc: NpcInstanceV1;
  getJournal: () => CoherenceJournal;
  playerCount: number;
  names: string[];
  victimName: string;
};

export type NpcCaseNotesFlowPayload = NpcDialogOpenPayload & {
  manifest: CaseManifestV1;
};

export class UIScene extends Phaser.Scene {
  private npcCaseNotesPanel!: NpcCaseNotesPanel;
  private npcDialogPanel!: NpcDialogPanel;
  private dialogEscHandler: ((e: KeyboardEvent) => void) | null = null;
  private timerText!: Phaser.GameObjects.Text;
  private zoneText!: Phaser.GameObjects.Text;
  private interiorLabelActive = false;
  private radioBtn!: Phaser.GameObjects.Text;
  private radioHalo!: Phaser.GameObjects.Graphics;
  private radioDispatchUnread = 0;
  private accusationSuppressesRadioGlow = false;
  private radioHaloBreathTween: Phaser.Tweens.Tween | null = null;
  private helpBtn!: Phaser.GameObjects.Text;
  private stopFollowBtn!: Phaser.GameObjects.Text;
  private unstuckBtn!: Phaser.GameObjects.Text;
  private unstuckOfferVisible = false;
  private followingLeader = false;
  private followerHudBlocked = false;
  private caseBtn!: Phaser.GameObjects.Text;
  private mapBtn!: Phaser.GameObjects.Text;
  private readonly onUnstuckVisibleBound = (visible: boolean): void => {
    this.unstuckOfferVisible = visible;
    this.unstuckBtn.setVisible(visible);
    this.unstuckBtn.setDepth(visible ? 2002 : 2000);
    this.applyHudLayout();
    this.time.delayedCall(0, () => this.applyHudLayout());
  };

  constructor() {
    super("UIScene");
  }

  create(): void {
    this.npcCaseNotesPanel = new NpcCaseNotesPanel(this);
    this.npcDialogPanel = new NpcDialogPanel(this);
    this.cameras.main.setViewport(0, 0, this.scale.width, this.scale.height);
    this.timerText = this.add
      .text(12, 10, "", {
        fontFamily: UI_FONT,
        fontSize: "20px",
        color: UI_TEXT.primary,
        fontStyle: "600",
        stroke: "#000000",
        strokeThickness: 3,
      })
      .setScrollFactor(0)
      .setDepth(2000);
    this.zoneText = this.add
      .text(this.scale.width - 120, 10, "Downtown", {
        fontFamily: UI_FONT,
        fontSize: "17px",
        color: UI_TEXT.link,
        fontStyle: "600",
        stroke: "#000000",
        strokeThickness: 2,
      })
      .setScrollFactor(0)
      .setDepth(2000);

    const btnStyle = {
      fontFamily: UI_FONT,
      fontSize: "16px",
      color: UI_TEXT.primary,
      fontStyle: "600",
      backgroundColor: "#1e2436ee",
      padding: { x: 14, y: 10 },
    };
    this.caseBtn = this.add
      .text(0, 0, "Case file", btnStyle)
      .setScrollFactor(0)
      .setDepth(2001)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this.game?.events.emit("ccc_open_journal"));
    this.mapBtn = this.add
      .text(0, 0, "Map", btnStyle)
      .setScrollFactor(0)
      .setDepth(2001)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this.game?.events.emit("ccc_open_journal_map"));

    this.onTimer(0);
    this.registry.events.on("ccc_timer", this.onTimer, this);
    this.registry.events.on("zone_changed", (z: string) => {
      if (this.interiorLabelActive) return;
      this.zoneText.setText(z === "downtown" ? "Downtown" : "Maple Heights");
    });
    this.registry.events.on(
      "interior_context_changed",
      (ctx: {
        mode: "outdoor" | "interior";
        buildingDisplayName?: string;
        outdoorZone?: string;
      }) => {
        if (ctx.mode === "interior" && ctx.buildingDisplayName) {
          this.interiorLabelActive = true;
          this.zoneText.setText(ctx.buildingDisplayName);
        } else {
          this.interiorLabelActive = false;
          const z = ctx.outdoorZone ?? "downtown";
          this.zoneText.setText(z === "downtown" ? "Downtown" : "Maple Heights");
        }
        this.layoutZoneLabel();
      }
    );
    this.scale.on("resize", this.onResize, this);
    this.game.events.on("ccc_open_npc_case_notes", this.onOpenNpcCaseNotes, this);
    this.game.events.on("ccc_open_npc_dialog", this.onOpenNpcDialog, this);
    this.game.events.on("ccc_force_close_npc_dialog", this.onForceCloseNpcDialog, this);
    this.events.on("shutdown", () => {
      this.registry.events.off("ccc_timer", this.onTimer, this);
      this.scale.off("resize", this.onResize, this);
      this.game.events.off("ccc_open_npc_case_notes", this.onOpenNpcCaseNotes, this);
      this.game.events.off("ccc_open_npc_dialog", this.onOpenNpcDialog, this);
      this.game.events.off("ccc_force_close_npc_dialog", this.onForceCloseNpcDialog, this);
      this.game.events.off("ccc_radio_dispatch_unread", this.onRadioDispatchUnread, this);
      this.game.events.off("ccc_accusation_phase_active", this.onAccusationPhaseActive, this);
      this.game.events.off("ccc_following_changed", this.onFollowingChanged, this);
      this.game.events.off("ccc_follower_hud_blocked", this.onFollowerHudBlocked, this);
      this.game.events.off("ccc_follow_apply_npc_mirror", this.onFollowNpcMirror, this);
      this.game.events.off("ccc_follow_nudge_received", this.onFollowNudgeReceived, this);
      this.radioHaloBreathTween?.stop();
      this.radioHaloBreathTween = null;
      this.unbindDialogEscape();
      this.npcCaseNotesPanel?.close();
      this.npcDialogPanel?.close();
      this.setHudChromeVisible(true);
      this.game.events.off("ccc_unstuck_visible", this.onUnstuckVisibleBound);
      this.unstuckBtn?.setVisible(false);
    });
    this.radioHalo = this.add
      .graphics()
      .setScrollFactor(0)
      .setDepth(1999)
      .setVisible(false);
    this.radioBtn = this.add
      .text(0, 0, "📻", {
        fontSize: "22px",
      })
      .setScrollFactor(0)
      .setDepth(2000)
      .setInteractive()
      .on("pointerdown", () => this.game?.events.emit("toggle_radio"));
    this.helpBtn = this.add
      .text(0, 0, "Help", {
        fontFamily: UI_FONT,
        fontSize: "16px",
        color: UI_TEXT.link,
        fontStyle: "600",
        backgroundColor: "#1e2436cc",
        padding: { x: 10, y: 6 },
      })
      .setScrollFactor(0)
      .setDepth(2000)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this.game?.events.emit("toggle_player_manual"));
    this.stopFollowBtn = this.add
      .text(0, 0, "Stop Following", {
        fontFamily: UI_FONT,
        fontSize: "16px",
        color: "#ffffff",
        fontStyle: "600",
        backgroundColor: "#b91c1c",
        padding: { x: 10, y: 6 },
      })
      .setScrollFactor(0)
      .setDepth(2000)
      .setVisible(false)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this.game?.events.emit("ccc_stop_following"));
    this.unstuckBtn = this.add
      .text(0, 0, "Unstuck", {
        fontFamily: UI_FONT,
        fontSize: "16px",
        color: UI_TEXT.link,
        fontStyle: "600",
        backgroundColor: "#1e2436cc",
        padding: { x: 10, y: 6 },
      })
      .setScrollFactor(0)
      .setDepth(2000)
      .setVisible(false)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => this.game?.events.emit("ccc_unstuck_request"));
    this.game.events.on("ccc_unstuck_visible", this.onUnstuckVisibleBound);

    this.game.events.on("ccc_radio_dispatch_unread", this.onRadioDispatchUnread, this);
    this.game.events.on("ccc_accusation_phase_active", this.onAccusationPhaseActive, this);
    this.game.events.on("ccc_following_changed", this.onFollowingChanged, this);
    this.game.events.on("ccc_follower_hud_blocked", this.onFollowerHudBlocked, this);
    this.game.events.on("ccc_follow_apply_npc_mirror", this.onFollowNpcMirror, this);
    this.game.events.on("ccc_follow_nudge_received", this.onFollowNudgeReceived, this);

    this.applyHudLayout();
  }

  private onFollowNudgeReceived = (): void => {
    if (this.npcDialogPanel.isOpen) {
      this.npcDialogPanel.flashNudgedAck();
      return;
    }
    if (this.npcCaseNotesPanel.isOpen) {
      this.npcCaseNotesPanel.flashNudgedAck();
      return;
    }
    if (isNarrativeModalOpen()) {
      flashNarrativeNudgedAck();
    }
  };

  private onFollowNpcMirror = (mirror: LeaderMirrorNpc): void => {
    const ccc = this.registry.get("ccc") as
      | {
          caseManifest?: CaseManifestV1;
          playerCount?: number;
          playerNames?: string[];
          getJournal?: () => Record<string, unknown>;
        }
      | undefined;
    const manifest = ccc?.caseManifest;
    if (!manifest) return;
    const npc = manifest.npcs.find((n) => n.instance_id === mirror.instanceId);
    if (!npc) return;

    if (!mirror.caseNotesOpen && !mirror.dialogOpen) {
      this.npcCaseNotesPanel.close();
      if (this.npcDialogPanel.isOpen) {
        this.npcDialogPanel.forceCloseWithoutProgress();
      }
      this.syncHudChromeForNpcOverlay();
      this.game.events.emit("ccc_follower_hud_blocked", false);
      return;
    }

    const names = ccc.playerNames ?? [];
    const victimName = manifest.victim.name;
    const dialogPayload: NpcDialogOpenPayload = {
      npc,
      getJournal: () =>
        (ccc.getJournal?.() ?? {}) as import("../lib/dialog-parser.js").CoherenceJournal,
      playerCount: ccc.playerCount ?? 1,
      names,
      victimName,
    };

    if (mirror.dialogOpen) {
      if (this.npcCaseNotesPanel.isOpen) this.npcCaseNotesPanel.close();
      if (this.npcDialogPanel.isOpen) {
        this.npcDialogPanel.syncMirror(mirror);
      } else {
        this.npcDialogPanel.openMirror({
          ...dialogPayload,
          onClose: () => {},
          mirror,
        });
      }
      this.game.events.emit("ccc_follower_hud_blocked", true);
      this.syncHudChromeForNpcOverlay();
      return;
    }

    if (mirror.caseNotesOpen) {
      if (this.npcDialogPanel.isOpen) {
        this.npcDialogPanel.forceCloseWithoutProgress();
      }
      if (!this.npcCaseNotesPanel.isOpen) {
        this.npcCaseNotesPanel.openMirror({
          npc,
          onBeginInterview: () => {},
          onCancel: () => {},
        });
      }
      this.game.events.emit("ccc_follower_hud_blocked", true);
      this.syncHudChromeForNpcOverlay();
    }
  };

  private onFollowingChanged = (following: boolean): void => {
    this.followingLeader = !!following;
    this.syncFollowHudLocks();
    this.applyHudLayout();
    if (!following) {
      if (this.npcDialogPanel.isOpen) this.npcDialogPanel.relayout();
      if (this.npcCaseNotesPanel.isOpen) this.npcCaseNotesPanel.relayout();
    }
  };

  private onFollowerHudBlocked = (blocked: boolean): void => {
    this.followerHudBlocked = !!blocked;
    this.syncStopFollowVisibility();
  };

  private syncStopFollowVisibility(): void {
    const show = this.followingLeader && !this.followerHudBlocked;
    this.stopFollowBtn.setVisible(show);
  }

  /** While following, only Stop Following is available in the HUD stack. */
  private syncFollowHudLocks(): void {
    const locked = this.followingLeader;
    this.caseBtn.setVisible(!locked);
    this.mapBtn.setVisible(!locked);
    this.radioBtn.setVisible(!locked);
    this.helpBtn.setVisible(!locked);
    if (locked) {
      this.unstuckBtn.setVisible(false);
    } else {
      this.unstuckBtn.setVisible(this.unstuckOfferVisible);
    }
    if (locked) {
      this.radioHalo?.setVisible(false);
      this.radioHaloBreathTween?.stop();
      this.radioHaloBreathTween = null;
    }
    this.syncStopFollowVisibility();
  }

  private hudChrome(): Phaser.GameObjects.Text[] {
    return [
      this.timerText,
      this.zoneText,
      this.caseBtn,
      this.mapBtn,
      this.radioBtn,
      this.helpBtn,
      this.stopFollowBtn,
      this.unstuckBtn,
    ];
  }

  private hudChromeObjects() {
    return {
      timer: this.timerText,
      caseBtn: this.caseBtn,
      mapBtn: this.mapBtn,
      radioBtn: this.radioBtn,
      helpBtn: this.helpBtn,
      stopFollowBtn: this.stopFollowBtn,
      unstuckBtn: this.unstuckBtn,
    };
  }

  private applyHudLayout(): HudLayoutMetrics {
    const metrics = layoutHudChrome(
      this.scale.width,
      this.scale.height,
      this.hudChromeObjects()
    );
    this.layoutZoneLabel();
    this.game?.events.emit("ccc_hud_layout", metrics);
    this.syncRadioHaloGeometry();
    this.syncRadioHaloVisibility();
    return metrics;
  }

  private layoutZoneLabel(): void {
    const pad = 12;
    const w = Math.min(280, Math.max(80, this.zoneText.width + 8));
    this.zoneText.setPosition(this.scale.width - pad - w, 10);
  }

  private onRadioDispatchUnread(payload: { count?: number }): void {
    this.radioDispatchUnread = Math.max(0, payload?.count ?? 0);
    this.syncRadioHaloVisibility();
  }

  private onAccusationPhaseActive(active: boolean): void {
    this.accusationSuppressesRadioGlow = active;
    this.syncRadioHaloVisibility();
  }

  private syncRadioHaloGeometry(): void {
    if (!this.radioHalo || !this.radioBtn) return;
    const cx = this.radioBtn.x + this.radioBtn.displayWidth * 0.5;
    const cy = this.radioBtn.y + this.radioBtn.displayHeight * 0.5;
    const fillW = 43;
    const fillH = 34;
    const innerW = 45;
    const innerH = 35;
    const outerW = 50;
    const outerH = 40;
    this.radioHalo.clear();
    this.radioHalo.fillGradientStyle(0xff2244, 0x89cff0, 0xff2244, 0x89cff0, 1);
    this.radioHalo.fillEllipse(cx, cy, fillW, fillH);
    this.radioHalo.lineStyle(4, 0xffffff, 0.9);
    this.radioHalo.strokeEllipse(cx, cy, innerW, innerH);
    this.radioHalo.lineStyle(2, 0x6688cc, 0.45);
    this.radioHalo.strokeEllipse(cx, cy, outerW, outerH);
  }

  private syncRadioHaloVisibility(): void {
    const show =
      this.radioDispatchUnread > 0 &&
      !this.accusationSuppressesRadioGlow &&
      this.radioBtn.visible;
    if (!show) {
      this.radioHaloBreathTween?.stop();
      this.radioHaloBreathTween = null;
      this.radioHalo?.setVisible(false);
      return;
    }
    this.syncRadioHaloGeometry();
    this.radioHalo.setVisible(true);
    this.radioHalo.setAlpha(0.75);
    this.radioHalo.setScale(1);
    if (!this.radioHaloBreathTween) {
      this.radioHaloBreathTween = this.tweens.add({
        targets: this.radioHalo,
        alpha: { from: 0.5, to: 0.95 },
        duration: 1100,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
    }
  }

  private setHudChromeVisible(visible: boolean): void {
    for (const obj of this.hudChrome()) {
      if (obj === this.unstuckBtn) continue;
      obj.setVisible(visible);
    }
    if (!visible) {
      this.unstuckBtn.setVisible(false);
    } else {
      this.unstuckBtn.setVisible(this.unstuckOfferVisible);
    }
    if (!visible) {
      this.radioHalo?.setVisible(false);
    } else {
      this.syncRadioHaloVisibility();
    }
  }

  private syncHudChromeForNpcOverlay(): void {
    const npcOpen = this.npcCaseNotesPanel.isOpen || this.npcDialogPanel.isOpen;
    const hideOnMobile = npcOpen && isMobileNpcOverlay(this.scale.width);
    this.setHudChromeVisible(!hideOnMobile);
    this.syncStopFollowVisibility();
  }


  private onResize(gameSize: Phaser.Structs.Size): void {
    this.cameras.main.setViewport(0, 0, gameSize.width, gameSize.height);
    layoutGameHud(this, gameSize.width, gameSize.height, DESIGN_WIDTH, DESIGN_HEIGHT);
    this.applyHudLayout();
    layoutClueToastIfVisible(this);
    if (this.npcCaseNotesPanel.isOpen) this.npcCaseNotesPanel.relayout();
    if (this.npcDialogPanel.isOpen) this.npcDialogPanel.relayout();
    this.syncHudChromeForNpcOverlay();
  }

  private bindDialogEscape(): void {
    this.unbindDialogEscape();
    this.dialogEscHandler = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.repeat) return;
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (this.npcCaseNotesPanel.isOpen) {
        e.preventDefault();
        this.npcCaseNotesPanel.cancel();
        return;
      }
      if (!this.npcDialogPanel.isOpen) return;
      e.preventDefault();
      this.npcDialogPanel.dismissWithoutProgress();
    };
    window.addEventListener("keydown", this.dialogEscHandler);
  }

  private unbindDialogEscape(): void {
    if (this.dialogEscHandler) {
      window.removeEventListener("keydown", this.dialogEscHandler);
      this.dialogEscHandler = null;
    }
  }

  private onForceCloseNpcDialog(): void {
    if (this.npcCaseNotesPanel.isOpen) {
      this.npcCaseNotesPanel.cancel();
    }
    if (!this.npcDialogPanel.isOpen) return;
    this.unbindDialogEscape();
    this.npcDialogPanel.forceCloseWithoutProgress();
    this.syncHudChromeForNpcOverlay();
  }

  private onOpenNpcCaseNotes(payload: NpcCaseNotesFlowPayload): void {
    const dialogPayload: NpcDialogOpenPayload = {
      npc: payload.npc,
      getJournal: payload.getJournal,
      playerCount: payload.playerCount,
      names: payload.names,
      victimName: payload.victimName,
    };
    const notesPayload: NpcCaseNotesOpenPayload = {
      npc: payload.npc,
      onBeginInterview: () => {
        this.bindDialogEscape();
        this.onOpenNpcDialog(dialogPayload);
        if (this.npcCaseNotesPanel.isOpen) this.npcCaseNotesPanel.close();
        this.syncHudChromeForNpcOverlay();
      },
      onCancel: () => {
        this.unbindDialogEscape();
        this.syncHudChromeForNpcOverlay();
        this.game.events.emit("ccc_npc_case_notes_closed");
        this.game.events.emit("ccc_leader_mirror_npc", {
          caseNotesOpen: false,
          dialogOpen: false,
          instanceId: payload.npc.instance_id,
          phase: "done",
          transcript: [],
          questionPage: 0,
        });
      },
    };
    this.npcCaseNotesPanel.open(notesPayload);
    this.syncHudChromeForNpcOverlay();
    this.bindDialogEscape();
    this.game.events.emit("ccc_npc_case_notes_shown", {
      instanceId: payload.npc.instance_id,
    });
    this.game.events.emit("ccc_leader_mirror_npc", {
      caseNotesOpen: true,
      dialogOpen: false,
      instanceId: payload.npc.instance_id,
      phase: "greeting",
      transcript: [],
      questionPage: 0,
    });
  }

  private onOpenNpcDialog(payload: NpcDialogOpenPayload): void {
    this.npcDialogPanel.open({
      ...payload,
      onClose: (result) => {
        this.unbindDialogEscape();
        this.syncHudChromeForNpcOverlay();
        this.game.events.emit("ccc_npc_dialog_closed", result);
      },
    });
    this.syncHudChromeForNpcOverlay();
    this.bindDialogEscape();
    this.game.events.emit("ccc_npc_dialog_shown", {
      instanceId: payload.npc.instance_id,
    });
  }

  private onTimer(elapsedSec: number): void {
    this.timerText.setText(formatElapsedClock(elapsedSec));
    this.timerText.setStyle({ color: "#ffffff" });
  }
}
